import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { Badge, Button } from '@tremor/react';
import { api } from '../lib/api';
import type { RegistryClassifiersResponse, RegistryClassifier, ClassifierVersionsResponse, ClassifierVersionDoc, VersionBundlesResponse } from '../types';
import { NewClassifierModal } from '../components/modals/NewClassifierModal';
import { NewVersionModal } from '../components/modals/NewVersionModal';

// ============================================================================
// Classifier Version Page — /classifiers/:key/:version
// ============================================================================

export function ClassifierVersionPage() {
  const { key, version } = useParams<{ key: string; version: string }>();
  const [settingCurrent, setSettingCurrent] = useState(false);
  const [editingOverrides, setEditingOverrides] = useState(false);
  const [editOverrides, setEditOverrides] = useState<Record<string, string>>({});
  const [savingOverrides, setSavingOverrides] = useState(false);

  const { data: classifierData, mutate: mutateClassifier } = useSWR<RegistryClassifier>(
    key ? `/api/registry/classifiers/${key}` : null,
    () => api.getRegistryClassifier(key!),
    { refreshInterval: 0 }
  );

  const { data, error, isLoading, mutate: mutateVersion } = useSWR<ClassifierVersionDoc>(
    key && version ? `/api/registry/classifiers/${key}/versions/${version}` : null,
    () => api.getClassifierVersion(key!, version!),
    { refreshInterval: 0 }
  );

  const { data: bundlesData } = useSWR<VersionBundlesResponse>(
    key && version ? `/api/registry/classifiers/${key}/versions/${version}/bundles` : null,
    () => api.getVersionBundles(key!, version!),
    { refreshInterval: 0 }
  );

  const isCurrent = classifierData?.currentVersion === version;

  const handleSetCurrent = async () => {
    setSettingCurrent(true);
    try {
      await api.setCurrentVersion(key!, version!);
      await mutateClassifier();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to set current version');
    } finally {
      setSettingCurrent(false);
    }
  };

  const startEditingOverrides = () => {
    if (!data) return;
    setEditOverrides({ ...(data.labelOverrides || {}) });
    setEditingOverrides(true);
  };

  const saveOverrides = async () => {
    setSavingOverrides(true);
    try {
      // Clean empty values and no-op self-maps
      const cleaned: Record<string, string> = {};
      for (const [label, target] of Object.entries(editOverrides)) {
        const t = target.trim();
        if (t && t !== label) cleaned[label] = t;
      }
      await api.updateVersion(key!, version!, { labelOverrides: cleaned });
      await mutateVersion();
      setEditingOverrides(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save label overrides');
    } finally {
      setSavingOverrides(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
          <span className="text-gray-500 animate-pulse">Loading version...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <p className="text-red-400">
            {error?.message || `Version '${version}' not found for classifier '${key}'`}
          </p>
          <Link to={`/classifiers/${key}`} className="text-blue-400 hover:underline text-sm mt-2 inline-block">
            &larr; Back to {key}
          </Link>
        </div>
      </div>
    );
  }

  const model = data.backend === 'openai'
    ? data.openai?.model || ''
    : data.backend === 'anthropic'
      ? data.anthropic?.model || ''
      : data.phi?.model || '';
  const isPromptMode = data.mode === 'prompt';
  const backendBadgeColor = data.backend === 'openai' ? 'blue' : data.backend === 'anthropic' ? 'orange' : 'purple';

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Link to="/classifiers" className="hover:text-gray-300">Classifiers</Link>
          <span>/</span>
          <Link to={`/classifiers/${key}`} className="hover:text-gray-300">{key}</Link>
          <span>/</span>
          <span className="text-gray-300">{data.version}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-white font-mono">{data.version}</h1>
              {isCurrent && (
                <span className="text-yellow-400" title="Current default version">&#9733;</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge color={backendBadgeColor} size="xs">{data.backend}</Badge>
              <Badge color={isPromptMode ? 'green' : 'gray'} size="xs">{data.mode || 'fine-tuned'}</Badge>
              {isCurrent && (
                <Badge color="blue" size="xs">current</Badge>
              )}
              <span className="text-sm text-gray-500">
                Created {new Date(data.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
          {!isCurrent && data.status === 'active' && (
            <Button size="sm" variant="secondary" onClick={handleSetCurrent} loading={settingCurrent}>
              Set as Current
            </Button>
          )}
        </div>
      </div>

      {/* Model */}
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
        <p className="text-xs text-gray-500 uppercase mb-1">Model ID</p>
        <code className="text-sm font-mono text-gray-300 break-all">{model || '--'}</code>
      </div>

      {/* Notes */}
      {data.notes && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
          <p className="text-xs text-gray-500 uppercase mb-1">Notes</p>
          <p className="text-sm text-gray-300">{data.notes}</p>
        </div>
      )}

      {/* Labels + Label Overrides */}
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-gray-500 uppercase">Labels ({data.labels.length})</p>
          {!editingOverrides ? (
            <Button size="xs" variant="secondary" onClick={startEditingOverrides}>
              Edit Label Overrides
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="xs" variant="secondary" onClick={() => setEditingOverrides(false)} disabled={savingOverrides}>
                Cancel
              </Button>
              <Button size="xs" onClick={saveOverrides} loading={savingOverrides}>
                Save
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Remaps the model's raw output label everywhere downstream — results, CSV exports, bundle filtering.
          To silence a label, remap it to <code className="text-gray-300">neutral</code>.
        </p>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800 text-left">
              <th className="py-1.5 pr-4 text-xs font-medium text-gray-500 uppercase w-1/3">Label</th>
              <th className="py-1.5 text-xs font-medium text-gray-500 uppercase">Remap To</th>
            </tr>
          </thead>
          <tbody>
            {data.labels.map(label => {
              const remapTo = data.labelOverrides?.[label] || '';
              return (
                <tr key={label} className="border-b border-gray-800/50">
                  <td className="py-1.5 pr-4">
                    <code className="text-xs font-mono text-blue-300">{label}</code>
                  </td>
                  <td className="py-1.5">
                    {editingOverrides ? (
                      <input
                        type="text"
                        value={editOverrides[label] || ''}
                        onChange={(e) => setEditOverrides({
                          ...editOverrides,
                          [label]: e.target.value,
                        })}
                        placeholder={label}
                        className="w-full text-xs px-2 py-1 rounded border border-gray-700 bg-gray-800 text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                      />
                    ) : (
                      <span className="text-xs">
                        {remapTo
                          ? <code className="font-mono text-amber-300">{remapTo}</code>
                          : <span className="text-gray-600">—</span>}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* In Bundles */}
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
        <p className="text-xs text-gray-500 uppercase mb-3">
          Used in Bundles{bundlesData ? ` (${bundlesData.bundles.length})` : ''}
        </p>
        {!bundlesData ? (
          <p className="text-xs text-gray-500 italic">Loading...</p>
        ) : bundlesData.bundles.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Not used in any bundles</p>
        ) : (
          <ul className="space-y-1.5">
            {bundlesData.bundles.map(bundle => (
              <li key={bundle.key} className="flex items-center gap-2 text-sm">
                <Link
                  to={`/bundles/${bundle.key}`}
                  className="text-blue-400 hover:underline"
                >
                  {bundle.name}
                </Link>
                {bundle.isDefault && <Badge color="blue" size="xs">default</Badge>}
                {!bundle.isActive && <Badge color="gray" size="xs">inactive</Badge>}
                {bundle.referenceType === 'implicit' && (
                  <span className="text-xs text-gray-500 italic">via family default</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Logit Bias */}
      {!isPromptMode && data.backend === 'openai' && data.openai?.logitBias && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
          <p className="text-xs text-gray-500 uppercase mb-1" title="Token IDs that constrain the model to only output valid label indices. Prevents the model from returning arbitrary text.">
            Logit Bias <span className="text-gray-600 cursor-help">&#9432;</span>
          </p>
          <code className="text-sm font-mono text-gray-400 break-all">
            {JSON.stringify(data.openai.logitBias, null, 2)}
          </code>
        </div>
      )}

      {/* Prompt template + parser (mode=prompt) */}
      {isPromptMode && data.prompt && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-4 space-y-3">
          <p className="text-xs text-gray-500 uppercase">Prompt</p>
          {data.prompt.systemPrompt && (
            <div>
              <p className="text-xs text-gray-500 mb-1">System</p>
              <pre className="text-xs font-mono text-gray-300 whitespace-pre-wrap bg-gray-950 rounded p-2 border border-gray-800">{data.prompt.systemPrompt}</pre>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500 mb-1">User template</p>
            <pre className="text-xs font-mono text-gray-300 whitespace-pre-wrap bg-gray-950 rounded p-2 border border-gray-800">{data.prompt.userPromptTemplate}</pre>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span>temperature: <span className="text-gray-300">{data.prompt.temperature ?? 0}</span></span>
            <span>max output tokens: <span className="text-gray-300">{data.prompt.maxOutputTokens ?? 64}</span></span>
            {data.prompt.maxInputTokens && (
              <span>max input tokens: <span className="text-gray-300">{data.prompt.maxInputTokens}</span></span>
            )}
          </div>
        </div>
      )}
      {isPromptMode && data.parser && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
          <p className="text-xs text-gray-500 uppercase mb-1">Parser</p>
          <p className="text-sm text-gray-300"><code className="font-mono text-blue-300">{data.parser.type}</code></p>
          {data.parser.config && Object.keys(data.parser.config).length > 0 && (
            <pre className="mt-2 text-xs font-mono text-gray-400 whitespace-pre-wrap bg-gray-950 rounded p-2 border border-gray-800">{JSON.stringify(data.parser.config, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Classifier Detail Page — /classifiers/:key
// ============================================================================

function VersionRow({
  classifierKey,
  version,
  isCurrent,
}: {
  classifierKey: string;
  version: ClassifierVersionDoc;
  isCurrent: boolean;
}) {
  const model = version.backend === 'openai'
    ? version.openai?.model || ''
    : version.backend === 'anthropic'
      ? version.anthropic?.model || ''
      : version.phi?.model || '';
  const modelShort = model.includes(':') ? model.split(':').slice(-2).join(':') : model;
  const backendBadgeColor = version.backend === 'openai' ? 'blue' : version.backend === 'anthropic' ? 'orange' : 'purple';

  return (
    <Link
      to={`/classifiers/${classifierKey}/${encodeURIComponent(version.version)}`}
      className={`block border rounded-lg overflow-hidden transition-colors cursor-pointer ${
        isCurrent ? 'border-blue-700 bg-blue-950/20 hover:border-blue-600' : 'border-gray-800 bg-gray-900 hover:border-gray-600'
      }`}
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isCurrent && (
              <span className="text-yellow-400 text-sm" title="Current default version">&#9733;</span>
            )}
            <span className="text-white font-semibold font-mono text-sm">{version.version}</span>
            {version.mode === 'prompt' && <Badge color="green" size="xs">prompt</Badge>}
            {isCurrent && (
              <Badge color="blue" size="xs">current</Badge>
            )}
            {version.status === 'deprecated' && (
              <Badge color="gray" size="xs">deprecated</Badge>
            )}
          </div>
          <div className="flex items-center shrink-0">
            <div className="w-20 flex justify-end">
              <Badge color={backendBadgeColor} size="xs">{version.backend}</Badge>
            </div>
            <span className="text-xs text-gray-500 w-20 text-right ml-1">
              {version.labels.length} labels
            </span>
            <span className="text-gray-600 text-sm ml-3">&rarr;</span>
          </div>
        </div>

        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-xs font-mono text-gray-500" title={model}>{modelShort}</span>
          {version.notes && (
            <>
              <span className="text-xs text-gray-600">&middot;</span>
              <span className="text-xs text-gray-500 italic truncate max-w-xs">"{version.notes}"</span>
            </>
          )}
          <span className="text-xs text-gray-600">&middot;</span>
          <span className="text-xs text-gray-600">
            {new Date(version.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ClassifierDetailPage() {
  const { key } = useParams<{ key: string }>();

  const [showNewVersion, setShowNewVersion] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const { data: classifierData, mutate: mutateClassifier } = useSWR<RegistryClassifier>(
    key ? `/api/registry/classifiers/${key}` : null,
    () => api.getRegistryClassifier(key!),
    { refreshInterval: 0 }
  );

  const { data, error, isLoading, mutate } = useSWR<ClassifierVersionsResponse>(
    key ? `/api/registry/classifiers/${key}/versions` : null,
    () => api.getClassifierVersions(key!),
    { refreshInterval: 0 }
  );

  const handleToggleActive = async () => {
    if (!classifierData) return;
    setTogglingActive(true);
    try {
      if (classifierData.isActive) {
        await api.deleteClassifier(key!);
      } else {
        await api.updateClassifier(key!, { isActive: true } as Partial<RegistryClassifier>);
      }
      await mutateClassifier();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update classifier');
    } finally {
      setTogglingActive(false);
    }
  };

  if (isLoading || !classifierData) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
          <span className="text-gray-500 animate-pulse">Loading classifier...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <p className="text-red-400">Error: {error.message}</p>
          <Link to="/classifiers" className="text-blue-400 hover:underline text-sm mt-2 inline-block">
            &larr; Back to classifiers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto space-y-4">
      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Link to="/classifiers" className="hover:text-gray-300">Classifiers</Link>
          <span>/</span>
          <span className="text-gray-300">{key}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {classifierData.name}
              <span className="text-gray-500 font-normal text-lg ml-2">({classifierData.key})</span>
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-500">{classifierData.classifierType}-level</span>
              {classifierData.description && (
                <>
                  <span className="text-xs text-gray-600">&middot;</span>
                  <span className="text-sm text-gray-500">{classifierData.description}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleActive}
              disabled={togglingActive}
              className="flex items-center gap-2 text-sm"
              title={classifierData.isActive ? 'Click to disable this classifier' : 'Click to enable this classifier'}
            >
              <span className={`text-xs ${classifierData.isActive ? 'text-emerald-400' : 'text-gray-500'}`}>
                {classifierData.isActive ? 'Enabled' : 'Disabled'}
              </span>
              <span
                className={`w-8 h-5 rounded-full relative transition-colors ${
                  classifierData.isActive ? 'bg-emerald-600' : 'bg-gray-700'
                } ${togglingActive ? 'opacity-50' : ''}`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                    classifierData.isActive ? 'left-3.5' : 'left-0.5'
                  }`}
                />
              </span>
            </button>
            <Button size="sm" onClick={() => setShowNewVersion(true)}>
              + New Version
            </Button>
          </div>
        </div>
      </div>

      {/* Version list */}
      {data && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm text-gray-400 uppercase tracking-wide">
              Version History ({data.count} versions)
            </h2>
            <span className="text-xs text-gray-500">
              Current: <code className="text-blue-400">{data.currentVersion || 'none'}</code>
            </span>
          </div>

          {data.versions.length === 0 ? (
            <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
              <p className="text-gray-500">No versions registered yet.</p>
              <Button size="sm" className="mt-3" onClick={() => setShowNewVersion(true)}>
                Register First Version
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {data.versions.map(v => (
                <VersionRow
                  key={v._id}
                  classifierKey={key!}
                  version={v}
                  isCurrent={v.version === data.currentVersion}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <NewVersionModal
        isOpen={showNewVersion}
        onClose={() => setShowNewVersion(false)}
        onSuccess={() => {
          mutate();
          mutateClassifier();
        }}
        classifierKey={key!}
        classifierName={classifierData.name}
        existingVersionCount={data?.versions.length ?? 0}
      />
    </div>
  );
}

// ============================================================================
// Classifier List Page — /classifiers
// ============================================================================

function ClassifierRow({
  classifier,
}: {
  classifier: RegistryClassifier;
}) {
  const vDoc = classifier.currentVersionDetails;
  const model = vDoc?.openai?.model || vDoc?.phi?.model || vDoc?.anthropic?.model || '';
  const modelShort = model.includes(':') ? model.split(':').slice(-2).join(':') : model;
  const backendBadgeColor = !vDoc ? 'gray'
    : vDoc.backend === 'openai' ? 'blue'
    : vDoc.backend === 'anthropic' ? 'orange'
    : 'purple';

  return (
    <Link
      to={`/classifiers/${classifier.key}`}
      className={`block bg-gray-900 rounded-lg border overflow-hidden transition-colors cursor-pointer ${
        classifier.isActive ? 'border-gray-800 hover:border-gray-600' : 'border-gray-800/50'
      }`}
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className={`text-base font-semibold ${classifier.isActive ? 'text-white' : 'text-gray-500'}`}>
                {classifier.name}
              </h2>
              <span className="text-xs text-gray-600">{classifier.classifierType}</span>
              {!classifier.isActive && (
                <Badge color="gray" size="xs">disabled</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {vDoc ? (
                <>
                  {vDoc.mode === 'prompt' && <Badge color="green" size="xs">prompt</Badge>}
                  <span className="text-xs text-gray-400 font-mono">{classifier.currentVersion}</span>
                  <span className="text-xs text-gray-600">&middot;</span>
                  <span className="text-xs text-gray-600 font-mono truncate" title={model}>{modelShort}</span>
                </>
              ) : (
                <span className="text-xs text-amber-400">No version set</span>
              )}
            </div>
          </div>
          <div className="flex items-center shrink-0">
            <div className="w-20 flex justify-end">
              {vDoc && <Badge color={backendBadgeColor} size="xs">{vDoc.backend}</Badge>}
            </div>
            <span className="text-xs text-gray-500 w-20 text-right ml-1">
              {vDoc ? `${vDoc.labels.length} labels` : ''}
            </span>
            <span className="text-gray-600 text-sm ml-3">&rarr;</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function ClassifiersPage() {
  const navigate = useNavigate();
  const [showNewClassifier, setShowNewClassifier] = useState(false);

  const { data, error, isLoading, mutate } = useSWR<RegistryClassifiersResponse>(
    '/api/registry/classifiers',
    () => api.getRegistryClassifiers(),
    { refreshInterval: 0 }
  );

  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8">
          <div className="animate-pulse flex items-center justify-center">
            <span className="text-gray-500">Loading classifier registry...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <p className="text-red-400">Error loading registry: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const activeCount = data.classifiers.filter(c => c.isActive).length;

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Classifier Registry</h1>
          <p className="text-gray-400 mt-1">
            Manage classifier families and model versions
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {activeCount} active / {data.count} total
          </span>
          <Button size="sm" onClick={() => setShowNewClassifier(true)}>
            + New Classifier
          </Button>
        </div>
      </div>

      {/* Classifier List */}
      <div className="space-y-2">
        {data.classifiers.map(classifier => (
          <ClassifierRow
            key={classifier.key}
            classifier={classifier}
          />
        ))}
      </div>

      {data.classifiers.length === 0 && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
          <p className="text-gray-500">No classifiers in the registry.</p>
          <p className="text-gray-600 text-sm mt-1">
            Run <code className="text-gray-400">node scripts/seed-classifiers.js</code> to populate from existing config.
          </p>
        </div>
      )}

      <NewClassifierModal
        isOpen={showNewClassifier}
        onClose={() => setShowNewClassifier(false)}
        onSuccess={(newKey) => {
          mutate();
          navigate(`/classifiers/${newKey}`);
        }}
      />
    </div>
  );
}
